import React, { useState, useEffect } from 'react';
import { api, Post } from '../lib/api';
import { TierBadge, TierType } from './TierBadge';
import { motion } from 'framer-motion';

interface ContentCardProps {
  post: Post;
  currentUserId?: string;
  onLikeToggle?: (postId: string, liked: boolean) => void;
  onDelete?: (postId: string) => void;
}

export const ContentCard: React.FC<ContentCardProps> = ({ 
  post, 
  currentUserId,
  onLikeToggle,
  onDelete
}) => {
  const [mediaUrl, setMediaUrl] = useState<string | null>(null);
  const [loadingMedia, setLoadingMedia] = useState<boolean>(false);
  const [liked, setLiked] = useState<boolean>(false);
  const [likeCount, setLikeCount] = useState<number>(post.like_count);
  const [togglingLike, setTogglingLike] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);

  // Check if current user likes this post.
  // In a full DB schema we'd return a `has_liked` field from `/api/posts/feed`.
  // Since we don't have it directly in our MVP backend response, we can track likes in local state
  // and check localStorage/session state if needed.
  // We'll initialize it to false and let the user toggle.
  
  useEffect(() => {
    let active = true;
    
    const loadMedia = async () => {
      if (post.locked || !post.media_key) {
        setMediaUrl(null);
        return;
      }
      
      try {
        setLoadingMedia(true);
        const url = await api.fetchMediaBlob(post.media_key, post.id);
        if (active) {
          setMediaUrl(url);
        }
      } catch (err) {
        console.error(`Failed to fetch media for post ${post.id}`, err);
      } finally {
        if (active) {
          setLoadingMedia(false);
        }
      }
    };

    loadMedia();

    return () => {
      active = false;
      // Revoke blob URL to avoid memory leak
      if (mediaUrl) {
        URL.revokeObjectURL(mediaUrl);
      }
    };
  }, [post.media_key, post.locked, post.id]);

  const handleLike = async () => {
    if (togglingLike) return;
    try {
      setTogglingLike(true);
      const res = await api.likePost(post.id);
      setLiked(res.liked);
      setLikeCount(prev => res.liked ? prev + 1 : Math.max(0, prev - 1));
      if (onLikeToggle) onLikeToggle(post.id, res.liked);
    } catch (err) {
      console.error('Failed to toggle like', err);
    } finally {
      setTogglingLike(false);
    }
  };

  const handleDelete = async () => {
    if (deleting) return;
    if (!window.confirm('Are you sure you want to delete this exclusive post?')) return;
    
    try {
      setDeleting(true);
      await api.deletePost(post.id);
      if (onDelete) onDelete(post.id);
    } catch (err) {
      console.error('Failed to delete post', err);
      alert('Failed to delete post');
    } finally {
      setDeleting(false);
    }
  };

  // Convert creator display name to handle or format wallet
  const creatorName = post.creator_name || 'Sole Creator';
  const isOwner = currentUserId === post.creator_id;

  // Render Lock Overlay based on required tier
  const tierThresholds: Record<string, string> = {
    bronze: '1,000',
    silver: '10,000',
    gold: '100,000',
    diamond: '1,000,000',
  };

  return (
    <motion.div 
      className="glass-card glass-card-interactive"
      style={{ padding: 0 }}
      whileHover={{ y: -6 }}
      transition={{ duration: 0.2 }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            borderRadius: '50%', 
            background: 'var(--gradient-primary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 800,
            fontSize: '1rem',
            overflow: 'hidden'
          }}>
            {post.creator_avatar ? (
              <img src={post.creator_avatar} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              creatorName.slice(0, 2).toUpperCase()
            )}
          </div>
          <div>
            <h4 style={{ fontSize: '0.95rem', fontWeight: 700 }}>{creatorName}</h4>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              @{post.creator_wallet.slice(0, 4)}...{post.creator_wallet.slice(-4)}
            </span>
          </div>
        </div>
        
        <TierBadge tier={post.tier} />
      </div>

      {/* Media area */}
      <div className="post-media-container">
        {post.locked ? (
          <div style={{ width: '100%', height: '100%', position: 'relative' }}>
            {/* Blurred placeholder image */}
            <div 
              className="post-media-image post-media-blur"
              style={{
                background: 'linear-gradient(135deg, rgba(255, 45, 120, 0.2), rgba(139, 92, 246, 0.2))',
                width: '100%',
                height: '100%'
              }}
            />
            <div className="post-lock-overlay">
              <div className="lock-icon-glow">
                <span style={{ fontSize: '1.75rem' }}>🔒</span>
              </div>
              <h3 style={{ fontSize: '1.2rem', fontFamily: 'var(--font-heading)' }}>
                Locked Content
              </h3>
              <p style={{ fontSize: '0.85rem', maxWidth: '240px' }}>
                Hold {tierThresholds[post.tier] || '0'} $SOLEM8 tokens in your wallet to unlock this post.
              </p>
            </div>
          </div>
        ) : loadingMedia ? (
          <div className="shimmer" style={{ width: '100%', height: '100%' }} />
        ) : mediaUrl ? (
          <img 
            src={mediaUrl} 
            alt="Sole Content" 
            className="post-media-image"
            loading="lazy"
          />
        ) : (
          <div style={{ 
            width: '100%', 
            height: '100%', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            color: 'var(--text-muted)'
          }}>
            Failed to load media
          </div>
        )}
      </div>

      {/* Footer / Description */}
      <div style={{ padding: '1rem' }}>
        <p style={{ fontSize: '0.95rem', color: 'var(--text-primary)', marginBottom: '0.75rem' }}>
          {post.locked ? '🔒 Unlock to read caption' : post.caption || '🦶 No caption provided'}
        </p>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid var(--border-glass)', paddingTop: '0.75rem', marginTop: '0.5rem' }}>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button 
              onClick={handleLike}
              disabled={post.locked || togglingLike}
              style={{
                background: 'none',
                border: 'none',
                cursor: post.locked ? 'not-allowed' : 'pointer',
                color: liked ? 'var(--accent-primary)' : 'var(--text-secondary)',
                display: 'flex',
                alignItems: 'center',
                gap: '0.35rem',
                fontSize: '0.9rem',
                fontWeight: 600,
                transition: 'var(--transition)'
              }}
            >
              <span>{liked ? '❤️' : '🤍'}</span>
              <span>{likeCount}</span>
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
              <span>👁️</span>
              <span>{post.view_count}</span>
            </div>
          </div>

          {isOwner && (
            <button
              onClick={handleDelete}
              disabled={deleting}
              style={{
                background: 'rgba(239, 68, 68, 0.1)',
                border: 'none',
                borderRadius: '6px',
                padding: '0.3rem 0.6rem',
                color: '#ef4444',
                fontSize: '0.75rem',
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'var(--transition)'
              }}
            >
              {deleting ? 'Deleting...' : 'Delete 🗑️'}
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
};
